import { RootActions } from "./types";
import {
  ONE_SIGNAL_APP_ID,
  VUEX_ASSIGNMENT_ACTION_NAMES,
  VUEX_ASSIGNMENT_NAMESPACE,
  VUEX_CONTENT_ACTION_NAMES,
  VUEX_CONTENT_NAMESPACE,
  VUEX_LOCALSTORAGE_KEYS,
  VUEX_PERSISTENT_LOCALSTORAGE_KEYS,
  VUEX_ROOT_ACTION_NAMES,
  VUEX_ROOT_MUTATION_NAMES
} from "@tt-app/constants";
import { api } from "@tt-app/api";
import { getAuthInfo } from "@tt-app/utils";
import { NotificationPermission } from "@tt-app/store/user/types";
import { v4 as uuidV4 } from "uuid";

const {
  CHECK_APP_HASH,
  CLEAR_LOCAL_STORAGE,
  DETERMINE_ANDROID_APP_VERSION,
  DETERMINE_IOS_APP_VERSION,
  FETCH_DETAILS,
  MANAGE_DEVICE_UUID,
  ONE_SIGNAL_DELETE_ALL_TAGS,
  ONE_SIGNAL_GET_CTA_INTERACTION_LOG,
  ONE_SIGNAL_GET_NOTIFICATION_PERMISSION,
  ONE_SIGNAL_INIT,
  ONE_SIGNAL_REMOVE_EXTERNAL_USER_ID,
  ONE_SIGNAL_RECORD_CTA_INTERACTION,
  ONE_SIGNAL_SEND_TAGS,
  ONE_SIGNAL_SET_EXTERNAL_USER_ID,
  ONE_SIGNAL_SET_SUBSCRIPTION,
  PUSH_NOTIFICATION_TOAST,
  SHIFT_NOTIFICATION_TOAST,
  SAVE_OPENED_CHAPTER,
  SAVE_PREVIOUS_ROUTE,
  SAVE_SELECTED_SKILL_LEVEL_INDEXES
} = VUEX_ROOT_ACTION_NAMES;

const {
  ANDROID_APP_VERSION,
  APP_VERSION_ALERT,
  DEVICE_UUID,
  IOS_APP_VERSION,
  MAINTENANCE_MODE,
  NOTIFICATION_TOASTS,
  OPENED_CHAPTERS,
  ONE_SIGNAL_CTA_INTERACTION,
  ONE_SIGNAL_INITIALIZED,
  ONE_SIGNAL_NOTIFICATION_PERMISSION,
  PREVIOUS_ROUTE,
  SELECTED_SKILL_LEVEL_INDEXES
} = VUEX_ROOT_MUTATION_NAMES;

export const rootActions: RootActions = {
  async [CHECK_APP_HASH]({ commit, state }) {
    try {
      const internalHash = state.internalHash;

      const {
        data: { commitHash: s3Hash, maintenanceMode }
      } = await api.app.gitInfo();

      commit(MAINTENANCE_MODE, maintenanceMode);

      const bothVersionsAvailable = internalHash !== undefined && s3Hash !== undefined;
      const versionsMismatch = internalHash !== s3Hash;

      console.info({ internalVersion: internalHash, s3Version: s3Hash });

      if (bothVersionsAvailable && versionsMismatch) {
        commit(APP_VERSION_ALERT, true);
      }
    } catch (error) {
      console.log(`${CHECK_APP_HASH} failed:`, error);
    }
  },

  [CLEAR_LOCAL_STORAGE]() {
    Object.values(VUEX_LOCALSTORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  },

  [DETERMINE_ANDROID_APP_VERSION]({ commit }) {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const allParams = Object.fromEntries(urlSearchParams.entries());
    const { androidAppVersion } = allParams;

    commit(ANDROID_APP_VERSION, { androidAppVersion });
  },

  async [DETERMINE_IOS_APP_VERSION]({ commit }) {
    const native = (window as any).Native;
    const appVersionCheck = new Promise((resolve, reject) => {
      native.getAppVersionInfo((version: any) => {
        if (version) {
          resolve(version.buildNumber);
        } else {
          resolve(undefined);
        }
      });
    });

    const version = await appVersionCheck;
    commit(IOS_APP_VERSION, { appVersion: version });
  },

  async [FETCH_DETAILS]({ dispatch }, payload) {
    if (payload.assignmentId) {
      await dispatch(
        `${VUEX_ASSIGNMENT_NAMESPACE}/${VUEX_ASSIGNMENT_ACTION_NAMES.DETAILS_REQUEST}`,
        payload
      );
    } else {
      await dispatch(
        `${VUEX_CONTENT_NAMESPACE}/${VUEX_CONTENT_ACTION_NAMES.COURSE_DETAILS_REQUEST}`,
        payload
      );
    }
  },

  async [MANAGE_DEVICE_UUID]({ commit, state }) {
    if (!state.deviceUuid) {
      const newDeviceUuid = uuidV4();

      commit(DEVICE_UUID, newDeviceUuid);
    }
  },

  async [ONE_SIGNAL_DELETE_ALL_TAGS]() {
    const oneSignal = (this as any)._vm.$OneSignal;

    const tags = await oneSignal.getTags();

    const tagKeys = Object.keys(tags);

    oneSignal.deleteTags(tagKeys);
  },

  async [ONE_SIGNAL_GET_CTA_INTERACTION_LOG]({ commit, state }: any) {
    const userUuid = state.user.profile.userUuid;

    const interactionRecordsString =
      localStorage.getItem(VUEX_PERSISTENT_LOCALSTORAGE_KEYS.CTA_INTERACTION) || "{}";

    const interactionRecords = JSON.parse(interactionRecordsString);

    const userInteractionRecord = {
      userUuid,
      status: interactionRecords[userUuid] || null
    };

    commit(ONE_SIGNAL_CTA_INTERACTION, userInteractionRecord);
  },

  async [ONE_SIGNAL_GET_NOTIFICATION_PERMISSION]({ commit }: any) {
    const oneSignal = (this as any)._vm.$OneSignal;

    const permission = await oneSignal.getNotificationPermission();

    commit(ONE_SIGNAL_NOTIFICATION_PERMISSION, permission);
  },

  async [ONE_SIGNAL_INIT]({ commit, state }: any) {
    const oneSignal = (this as any)._vm.$OneSignal;

    if (!state.oneSignal.initialized) {
      await oneSignal.init({ appId: ONE_SIGNAL_APP_ID });

      commit(ONE_SIGNAL_INITIALIZED);
    }
  },

  async [ONE_SIGNAL_REMOVE_EXTERNAL_USER_ID]() {
    const oneSignal = (this as any)._vm.$OneSignal;

    await oneSignal.removeExternalUserId();
  },

  async [ONE_SIGNAL_SEND_TAGS]({ rootGetters }: any) {
    try {
      const authInfo = await getAuthInfo(rootGetters);

      await api.user.tagAlias({ authInfo });
    } catch (error) {
      console.log({ error });
    }
  },

  async [ONE_SIGNAL_SET_EXTERNAL_USER_ID]({ state }: any) {
    const oneSignal = (this as any)._vm.$OneSignal;

    const userUuid = state.user.profile.userUuid;

    await oneSignal.setExternalUserId(userUuid);
  },

  async [ONE_SIGNAL_RECORD_CTA_INTERACTION]({ commit, dispatch, state }: any, payload) {
    const oneSignal = (this as any)._vm.$OneSignal;

    await oneSignal.showNativePrompt();
    if (window.Notification) {
      await Notification.requestPermission();
    }
    await dispatch(ONE_SIGNAL_GET_NOTIFICATION_PERMISSION);

    const userUuid = state.user.profile.userUuid;
    const status = payload || state.oneSignal.notificationPermission;

    const interactionRecord = {
      userUuid,
      status
    };

    commit(ONE_SIGNAL_CTA_INTERACTION, interactionRecord);
    dispatch(ONE_SIGNAL_SET_SUBSCRIPTION);
    dispatch(ONE_SIGNAL_SEND_TAGS);
  },

  async [ONE_SIGNAL_SET_SUBSCRIPTION]({ state }: any, payload: boolean) {
    const oneSignal = (this as any)._vm.$OneSignal;

    if (payload !== undefined) {
      oneSignal.setSubscription(payload);

      return;
    }

    const ctaInteractionLog = state.oneSignal.ctaInteraction;
    const notificationPermissions = state.oneSignal.notificationPermission;

    if (
      ctaInteractionLog === NotificationPermission.GRANTED &&
      notificationPermissions === NotificationPermission.GRANTED
    ) {
      oneSignal.setSubscription(true);
    } else {
      oneSignal.setSubscription(false);
    }
  },

  async [SHIFT_NOTIFICATION_TOAST]({ commit, state }: any) {
    const newNotificationToasts: any[] = [...state.notificationToasts.slice(1)];

    commit(NOTIFICATION_TOASTS, newNotificationToasts);
  },

  async [PUSH_NOTIFICATION_TOAST](
    { commit, state }: any,
    payload: { text: string; timespan: number }
  ) {
    commit(NOTIFICATION_TOASTS, [...state.notificationToasts, payload]);
  },

  [SAVE_OPENED_CHAPTER]({ commit, state, rootGetters }, payload) {
    const aliasUuid = rootGetters.auth_info.aliasUuid;

    const openedChapters = { ...state.course.openedChapters };
    const openedChaptersByAliasUuid = { ...openedChapters[aliasUuid] };
    const openedChaptersByCourseUuid = { ...openedChaptersByAliasUuid[payload.courseUuid] };

    if (payload.open) {
      openedChaptersByCourseUuid[payload.chapterUuid] = true;
    } else {
      delete openedChaptersByCourseUuid[payload.chapterUuid];
    }

    const openedChapterCount = Object.keys(openedChaptersByCourseUuid).length;
    if (openedChapterCount) {
      openedChaptersByAliasUuid[payload.courseUuid] = openedChaptersByCourseUuid;
    } else {
      delete openedChaptersByAliasUuid[payload.courseUuid];
    }

    const courseWithOpenChapterCount = Object.keys(openedChaptersByAliasUuid).length;
    if (courseWithOpenChapterCount) {
      openedChapters[aliasUuid] = openedChaptersByAliasUuid;
    } else {
      delete openedChapters[aliasUuid];
    }

    commit(OPENED_CHAPTERS, openedChapters);
  },
  [SAVE_PREVIOUS_ROUTE]({ commit }, prevRoute) {
    commit(PREVIOUS_ROUTE, prevRoute);
  },
  [SAVE_SELECTED_SKILL_LEVEL_INDEXES]({ commit }, payload) {
    commit(SELECTED_SKILL_LEVEL_INDEXES, payload);
  }
};
